import { useState, useEffect, lazy, Suspense } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { SDLCPhaseTree } from './SDLCPhaseTree';
import { StudioUX, type StudioMode } from '../Studio/StudioUX';
const FileTree = lazy(() =>
  import('./FileTree').then(m => ({ default: m.FileTree }))
);
const CodeEditor = lazy(() =>
  import('./CodeEditor').then(m => ({ default: m.CodeEditor }))
);
const AIChatPanel = lazy(() =>
  import('./AIChatPanel').then(m => ({ default: m.AIChatPanel }))
);
// PlanModeTreePanel removed - unused import
const ExecutionSidebar = lazy(() =>
  import('../Layout/Sidebars/ExecutionSidebar').then(m => ({
    default: m.ExecutionSidebar,
  }))
);
const PlanSectionEditor = lazy(() =>
  import('./PlanSectionEditor').then(m => ({ default: m.PlanSectionEditor }))
);
const RequirementsForm = lazy(() =>
  import('../PlanMode/RequirementsForm').then(m => ({
    default: m.RequirementsForm,
  }))
);
const WireframeEditor = lazy(() =>
  import('../PlanMode/WireframeEditor').then(m => ({
    default: m.WireframeEditor,
  }))
);
const SchemaDesigner = lazy(() =>
  import('../PlanMode/SchemaDesigner').then(m => ({
    default: m.SchemaDesigner,
  }))
);
const UserStoriesViewer = lazy(() =>
  import('../PlanMode/UserStoriesViewer').then(m => ({
    default: m.UserStoriesViewer,
  }))
);
const StandardsViewer = lazy(() =>
  import('../PlanMode/StandardsViewer').then(m => ({
    default: m.StandardsViewer,
  }))
);
const ThemeDesigner = lazy(() =>
  import('../PlanMode/ThemeDesigner').then(m => ({ default: m.ThemeDesigner }))
);
const DesignSystemConfig = lazy(() =>
  import('../PlanMode/DesignSystemConfig').then(m => ({
    default: m.DesignSystemConfig,
  }))
);
const ProjectTimeline = lazy(() =>
  import('../PlanMode/ProjectTimeline').then(m => ({
    default: m.ProjectTimeline,
  }))
);
const ConsolePanel = lazy(() =>
  import('./ConsolePanel').then(m => ({ default: m.ConsolePanel }))
);
const OrchestratorPanel = lazy(() =>
  import('./OrchestratorPanel').then(m => ({ default: m.OrchestratorPanel }))
);
import { IDESettingsModal } from './IDESettingsModal';
import {
  FileText,
  Terminal,
  Settings,
  Maximize2,
  Minimize2,
  ExternalLink,
  File,
  Layout,
  Hammer,
  Lock,
  Unlock,
  X,
  GitPullRequest,
  // Code2 removed - unused import
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { lazyLogger } from '../../services/lazyLogger';
import { logger } from '../../services/loggingService';
// Unified UI Components
import { Heading, Text } from '../ui/Typography';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { LoadingState } from '../Common/LoadingState';
import { cn } from '../../utils/cn';
import { spacing } from '../../utils/spacing';
interface IDEProps {
  projectId?: string;
}

interface SelectedPlanSection {
  sectionId: string;
  sectionTitle: string;
  content: any;
  sectionType?: string; // Added to determine which editor component to load
}

export function IDE({ projectId }: IDEProps) {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedPlanSection, setSelectedPlanSection] =
    useState<SelectedPlanSection | null>(null);
  const [showConsole, setShowConsole] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [buildMode, setBuildMode] = useState<'plan' | 'build'>('plan');
  const [blueprintLocked, setBlueprintLocked] = useState(false);
  const [buildSessionId, setBuildSessionId] = useState<string | null>(null);
  const [isStartingBuild, setIsStartingBuild] = useState(false);
  const [chatContext, setChatContext] = useState<any>(null);
  const [sdlcPhases, setSdlcPhases] = useState<any[]>([]);
  const [phaseExecutions, setPhaseExecutions] = useState<any[]>([]);
  const [currentTicket, setCurrentTicket] = useState<any>(null);
  const [showStudio, setShowStudio] = useState(false);
  const [studioConfig, setStudioConfig] = useState<{
    phaseId?: string;
    stepIndex?: number;
    mode?: StudioMode;
    content?: any;
  }>({});
  const [editorContent, setEditorContent] = useState<any>('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCreatePRDialog, setShowCreatePRDialog] = useState(false);
  const [prTitle, setPRTitle] = useState('');
  const [prDescription, setPRDescription] = useState('');
  const [prBranch, setPRBranch] = useState('feature/new-feature');
  const [prBaseBranch, setPRBaseBranch] = useState('main');
  const [isCreatingPR, setIsCreatingPR] = useState(false);

  useEffect(() => {
    if (!user || !projectId) {
      toast.error('Project not found');
      return;
    }
    checkBlueprintStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId]);

  // Load SDLC phases when in build mode
  useEffect(() => {
    if (buildMode === 'build' && projectId) {
      loadSdlcPhases();
    }
  }, [buildMode, projectId]);

  const loadSdlcPhases = async () => {
    if (!projectId) return;

    try {
      // Get current ticket for the project (or first active ticket)
      const { data: tickets } = await supabase
        .from('tickets')
        .select('id, title, jira_key, status')
        .eq('project_id', projectId)
        .in('status', ['todo', 'in_progress'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (tickets && tickets.length > 0) {
        const ticket = tickets[0];
        setCurrentTicket(ticket);

        // Load SDLC phases for this ticket
        const { data: phases, error: phasesError } = await supabase
          .from('sdlc_phases')
          .select('*')
          .eq('ticket_id', ticket.id)
          .order('phase_order', { ascending: true });

        if (phasesError) {
          logger.error('Error loading SDLC phases', { error: phasesError });
        } else {
          setSdlcPhases(phases || []);
        }

        // Load phase executions
        const { data: executions, error: execError } = await supabase
          .from('phase_executions')
          .select('*')
          .eq('ticket_id', ticket.id)
          .order('created_at', { ascending: false });

        if (execError) {
          logger.error('Error loading phase executions', { error: execError });
        } else {
          setPhaseExecutions(executions || []);
        }
      }
    } catch (error) {
      logger.error('Error loading SDLC data', { error, projectId });
    }
  };

  const checkBlueprintStatus = async () => {
    if (!projectId) return;
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('blueprint_locked_at')
        .eq('id', projectId)
        .maybeSingle();

      if (error) {
        const { logger } = await import('../../services/loggingService');
        logger.error('Error checking blueprint lock status', {
          error,
          projectId,
        });
        throw error;
      }
      setBlueprintLocked(!!data?.blueprint_locked_at);
    } catch (error) {
      lazyLogger.error('Error checking blueprint status:', error);
    }
  };

  if (!user) {
    return (
      <div className='h-full flex items-center justify-center'>
        <div className='text-center'>
          <FileText className='w-16 h-16 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
            Authentication Required
          </h3>
          <p className='text-gray-600 dark:text-gray-400'>
            Please sign in to use IDE mode.
          </p>
        </div>
      </div>
    );
  }

  // If no projectId, show project selector
  if (!projectId) {
    return (
      <div className='h-full flex items-center justify-center bg-background-secondary'>
        <div className='text-center max-w-md p-8'>
          <FileText className='w-16 h-16 text-gray-400 mx-auto mb-4' />
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
            Select a Project
          </h3>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            Please select a project from the dashboard to open in the IDE, or create a new project.
          </p>
          <Button onClick={() => window.location.href = '/dashboard'}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const handleFileSelect = (filePath: string) => {
    setSelectedFile(filePath);
    setSelectedPlanSection(null); // Clear plan section when file is selected
  };

  // Plan section selection handler - reserved for future Plan Mode integration
  // const handlePlanSectionSelect = async (sectionId: string, sectionData: any) => {
  //   // sectionData can be the old format (just content) or new format (object with sectionType, sectionTitle, etc.)
  //   const isNewFormat =
  //     sectionData && typeof sectionData === 'object' && sectionData.sectionType;
  //
  //   const sectionTitle = isNewFormat
  //     ? sectionData.sectionTitle || sectionId
  //     : sectionId;
  //
  //   const sectionType = isNewFormat
  //     ? sectionData.sectionType
  //     : getSectionTypeFromId(sectionId);
  //
  //   let content = isNewFormat ? sectionData.content : sectionData;
  //
  //   // If no content provided, try to load from blueprint
  //   if (!content || (typeof content === 'object' && Object.keys(content).length === 0)) {
  //     const loadedContent = await loadBlueprintSection(sectionId);
  //     if (loadedContent) {
  //       content = loadedContent;
  //     }
  //   }
  //
  //   setSelectedPlanSection({
  //     sectionId,
  //     sectionTitle,
  //     content,
  //     sectionType, // Add section type so editor knows what to load
  //   });
  //   setSelectedFile(null); // Clear file selection when plan section is selected
  // };

  // Helper to determine section type from section ID
  // Note: Used by commented-out handlePlanSectionSelect function (lines 218-248)
  // Kept for future Plan Mode integration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getSectionTypeFromId = (sectionId: string): string => {
    const typeMap: Record<string, string> = {
      scope: 'requirements',
      features: 'requirements',
      'user-stories': 'user-stories',
      stakeholders: 'requirements',
      architecture: 'architecture',
      'tech-stack': 'tech-stack',
      schema: 'schema',
      database: 'schema',
      wireframes: 'wireframes',
      'api-design': 'api-design',
      standards: 'standards',
      security: 'security',
      theme: 'theme',
      'design-system': 'design-system',
      timeline: 'timeline',
    };

    for (const [key, value] of Object.entries(typeMap)) {
      if (sectionId.includes(key) || sectionId === key) {
        return value;
      }
    }

    return 'requirements'; // Default
  };

  const handleChatContextUpdate = (context: any) => {
    setChatContext(context);
  };

  const handleFileChange = async (filePath: string, content: string) => {
    try {
      if (!projectId) {
        logger.warn('Cannot track file change without project ID', {
          filePath,
        });
        return;
      }

      // Track file change in file_registry
      const { error: registryError } = await supabase
        .from('file_registry')
        .upsert(
          {
            project_id: projectId,
            file_path: filePath,
            content,
            status: 'modified',
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'project_id,file_path',
          }
        );

      if (registryError) {
        logger.error('Error tracking file change in registry', {
          error: registryError,
          filePath,
        });
      }

      // Trigger real-time analysis if available
      try {
        const { RealtimeAnalysisService } = await import(
          '../../services/realtimeAnalysisService'
        );
        await RealtimeAnalysisService.analyzeFile(projectId, filePath);
        logger.info('File analysis triggered', { filePath });
      } catch (analysisError) {
        logger.warn('Real-time analysis not available', {
          error: analysisError,
        });
      }

      logger.info('File change tracked', {
        filePath,
        contentLength: content.length,
      });
    } catch (error) {
      logger.error('Error handling file change', { error, filePath });
    }
  };

  const handleFileSave = async (filePath: string, content: string) => {
    try {
      if (!projectId) {
        toast.error('Cannot save file without project');
        return;
      }

      // Save to file_registry with 'saved' status
      const { error: saveError } = await supabase.from('file_registry').upsert(
        {
          project_id: projectId,
          file_path: filePath,
          content,
          status: 'saved',
          last_modified_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'project_id,file_path',
        }
      );

      if (saveError) {
        logger.error('Error saving file to registry', {
          error: saveError,
          filePath,
        });
        toast.error(`Failed to save ${filePath}`);
        return;
      }

      // Trigger compliance check in build mode
      if (buildMode === 'build' && blueprintLocked) {
        try {
          const { ComplianceCheckService } = await import(
            '../../services/complianceCheckService'
          );
          const complianceResult =
            await ComplianceCheckService.checkFileCompliance(
              projectId,
              filePath,
              content
            );

          if (!complianceResult.compliant) {
            const violationCount = complianceResult.violations?.length || 0;
            toast(
              `File saved with ${violationCount} compliance ${violationCount === 1 ? 'violation' : 'violations'}`,
              {
                duration: 4000,
                icon: '⚠️',
                style: {
                  background: '#FEF3C7',
                  color: '#92400E',
                },
              }
            );
            logger.warn('Compliance violations detected', {
              filePath,
              violations: complianceResult.violations,
            });
          } else {
            toast.success(`${filePath.split('/').pop()} saved successfully`);
            logger.info('File saved and passed compliance', { filePath });
          }
        } catch (complianceError) {
          logger.warn('Compliance check failed', {
            error: complianceError,
            filePath,
          });
          toast.success(
            `${filePath.split('/').pop()} saved (compliance check skipped)`
          );
        }
      } else {
        toast.success(`${filePath.split('/').pop()} saved successfully`);
        logger.info('File saved', { filePath });
      }

      // Update file tree state if needed
      if (selectedFile === filePath) {
        // Refresh the file content in state if needed
        setSelectedFile(filePath);
      }
    } catch (error) {
      logger.error('Error saving file', { error, filePath });
      toast.error('Failed to save file');
    }
  };

  // Load blueprint section data from database
  // Note: Used by commented-out handlePlanSectionSelect function (lines 218-248)
  // Kept for future Plan Mode integration
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const loadBlueprintSection = async (sectionId: string): Promise<any> => {
    if (!projectId) return null;

    try {
      // Try to load from project_blueprints table
      const { data: blueprint, error } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        logger.error('Error loading blueprint section', { error, sectionId });
        return null;
      }

      if (blueprint?.content) {
        // Extract section-specific content
        const content = blueprint.content;
        if (sectionId === 'scope' && content.scope) {
          return content.scope;
        }
        if (sectionId === 'wireframes' && content.wireframes) {
          return content.wireframes;
        }
        if (sectionId === 'requirements' && content.requirements) {
          return content.requirements;
        }
        // Return full content if section-specific not found
        return content;
      }

      // Fallback: try to load from specific tables
      if (sectionId === 'wireframes') {
        const { data: wireframes } = await supabase
          .from('wireframes')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });
        return wireframes || [];
      }

      if (sectionId === 'scope' || sectionId === 'requirements') {
        const { data: requirements } = await supabase
          .from('atomic_requirements')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });
        return requirements || [];
      }

      return null;
    } catch (error) {
      logger.error('Error loading blueprint section', {
        error,
        sectionId,
        projectId,
      });
      return null;
    }
  };

  const handlePlanSectionSave = (sectionId: string, updatedContent: any) => {
    lazyLogger.debug('Plan section saved', { sectionId, updatedContent });
    toast.success('Plan section saved successfully');
    // Update the selected plan section with new content
    if (selectedPlanSection) {
      setSelectedPlanSection({
        ...selectedPlanSection,
        content: updatedContent,
      });
    }
  };

  const handleClosePlanSection = () => {
    setSelectedPlanSection(null);
  };

  // Render the appropriate editor component based on section type
  const renderSectionEditor = (section: SelectedPlanSection) => {
    if (!projectId || !user?.id) {
      return (
        <div className='p-4 text-center text-gray-500'>
          Missing project or user information
        </div>
      );
    }

    const sectionType = section.sectionType || 'requirements';

    switch (sectionType) {
      case 'requirements': {
        // Load scope/requirements from blueprint content
        // If content exists, show it in editor; otherwise show form to create
        const hasContent =
          section.content &&
          ((typeof section.content === 'string' &&
            section.content.trim() !== '') ||
            (typeof section.content === 'object' &&
              Object.keys(section.content).length > 0));

        if (
          !hasContent &&
          (section.sectionId === 'scope' || section.sectionId === 'features')
        ) {
          // Show form if no content exists
          return (
            <div className='h-full flex flex-col'>
              <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
                <h3 className='font-semibold text-gray-900 dark:text-white'>
                  {section.sectionTitle}
                </h3>
                <button
                  onClick={handleClosePlanSection}
                  className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
                >
                  <X className='w-4 h-4' />
                </button>
              </div>
              <div className='flex-1 overflow-y-auto'>
                <RequirementsForm
                  projectId={projectId}
                  userId={user.id}
                  onBlueprintGenerated={() => {}}
                />
              </div>
            </div>
          );
        }
        // Show editor with existing content - will load from blueprint if needed
        return (
          <PlanSectionEditor
            sectionId={section.sectionId}
            sectionTitle={section.sectionTitle}
            content={section.content}
            projectId={projectId}
            userId={user.id}
            onClose={handleClosePlanSection}
            onSave={handlePlanSectionSave}
          />
        );
      }

      case 'user-stories':
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <UserStoriesViewer projectId={projectId} />
            </div>
          </div>
        );

      case 'wireframes':
        // Load wireframes for the project and show gallery/editor
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle || 'Wireframes'}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <WireframeEditor
                projectId={projectId}
                userId={user.id}
                wireframeId={
                  section.content?.wireframeId ||
                  section.content?.id ||
                  undefined
                }
                onSave={(wireframe: any) =>
                  handlePlanSectionSave(section.sectionId, wireframe)
                }
                onClose={handleClosePlanSection}
              />
            </div>
          </div>
        );

      case 'schema':
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <SchemaDesigner projectId={projectId} />
            </div>
          </div>
        );

      case 'standards':
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <StandardsViewer projectId={projectId} />
            </div>
          </div>
        );

      case 'theme':
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <ThemeDesigner projectId={projectId} />
            </div>
          </div>
        );

      case 'design-system':
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <DesignSystemConfig
                projectId={projectId}
                blueprintId={section.sectionId}
                onComplete={() => {}}
              />
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className='h-full flex flex-col'>
            <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between'>
              <h3 className='font-semibold text-gray-900 dark:text-white'>
                {section.sectionTitle}
              </h3>
              <button
                onClick={handleClosePlanSection}
                className='p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded'
              >
                <X className='w-4 h-4' />
              </button>
            </div>
            <div className='flex-1 overflow-y-auto'>
              <ProjectTimeline projectId={projectId} />
            </div>
          </div>
        );

      default:
        // Default to generic PlanSectionEditor for unknown types
        return (
          <PlanSectionEditor
            sectionId={section.sectionId}
            sectionTitle={section.sectionTitle}
            content={section.content}
            projectId={projectId}
            userId={user.id}
            onClose={handleClosePlanSection}
            onSave={handlePlanSectionSave}
          />
        );
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const openPreview = () => {
    if (!projectId) return;
    const previewUrl = `${window.location.origin}/preview/${projectId}`;
    window.open(previewUrl, '_blank');
  };

  const toggleConsole = () => {
    setShowConsole(!showConsole);
  };

  const handleContentUpdate = (content: any, mode?: string) => {
    setEditorContent(content);
    // Update studio config if mode changed
    if (mode) {
      setStudioConfig(prev => ({ ...prev, mode: mode as StudioMode, content }));
    }
  };

  const handleOpenStudio = (phaseId: string, stepIndex?: number) => {
    // Determine mode based on phase
    let mode: StudioMode = 'code';
    if (phaseId.includes('ui-ux') || phaseId.includes('wireframe')) {
      mode = 'wireframe';
    } else if (phaseId.includes('architecture')) {
      mode = 'architecture';
    } else if (phaseId.includes('database')) {
      mode = 'database';
    } else if (phaseId.includes('documentation')) {
      mode = 'document';
    } else if (phaseId.includes('design')) {
      mode = 'design';
    }

    setStudioConfig({
      phaseId,
      stepIndex,
      mode,
      content: editorContent || '',
    });
    setShowStudio(true);
  };

  const startBuildMode = async () => {
    if (!projectId || !blueprintLocked) return;

    setIsStartingBuild(true);
    try {
      // Create a new build session
      const { data: session, error } = await supabase
        .from('build_mode_sessions')
        .insert({
          project_id: projectId,
          status: 'active',
          started_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) throw error;

      setBuildSessionId(session.id);
      setBuildMode('build');
      toast.success('Build Mode activated! AI Orchestrator is ready.');
    } catch (error) {
      lazyLogger.error('Error starting build mode:', error);
      toast.error('Failed to start build mode');
    } finally {
      setIsStartingBuild(false);
    }
  };

  const stopBuildMode = () => {
    setBuildMode('plan');
    setBuildSessionId(null);
    toast.success('Returned to Plan Mode');
  };

  const handleCreatePR = async () => {
    try {
      setIsCreatingPR(true);

      // Get project GitHub details
      const { data: project } = await supabase
        .from('projects')
        .select('github_owner, github_repo_name')
        .eq('id', projectId)
        .maybeSingle();

      if (!project || !project.github_owner || !project.github_repo_name) {
        toast.error('GitHub repository not configured for this project');
        return;
      }

      // Import GitHubService
      const { GitHubService } = await import('../../services/github/GitHubService');

      // Create the pull request
      const result = await GitHubService.createPullRequest(user!.id, {
        repo: project.github_repo_name,
        owner: project.github_owner,
        head: prBranch,
        base: prBaseBranch,
        title: prTitle,
        body: prDescription,
        draft: false,
      });

      if (result.success && result.prUrl) {
        toast.success(`Pull Request #${result.prNumber} created successfully!`);
        setShowCreatePRDialog(false);
        // Reset form
        setPRTitle('');
        setPRDescription('');
        setPRBranch('feature/new-feature');
        // Open PR in new tab
        window.open(result.prUrl, '_blank');
      } else {
        toast.error(result.error || 'Failed to create pull request');
      }
    } catch (error) {
      logger.error('Error creating pull request', { error, projectId });
      toast.error('Failed to create pull request');
    } finally {
      setIsCreatingPR(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background-secondary">
      {/* Top Bar */}
      <div className='flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'>
        <div
          className="flex items-center justify-between"
          style={{ padding: spacing.section.header }}
        >
          <div className="flex items-center gap-4">
            <Heading level={2} className="flex items-center gap-2">
              IDE Mode
              {buildMode === 'build' && (
                <Badge variant="default" className="text-xs">
                  Build Active
                </Badge>
              )}
            </Heading>

            {/* Mode Toggle */}
            <div
              className="flex items-center gap-1 p-1 rounded-lg"
              style={{
                backgroundColor: 'rgb(var(--color-background-secondary))',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <Button
                variant={buildMode === 'plan' ? 'default' : 'ghost'}
                size="sm"
                onClick={stopBuildMode}
                disabled={buildMode === 'plan'}
                className="px-3 py-1.5"
              >
                <div className="flex items-center gap-1.5">
                  <Layout className="w-3.5 h-3.5" />
                  Plan
                </div>
              </Button>
              <Button
                variant={buildMode === 'build' ? 'default' : 'ghost'}
                size="sm"
                onClick={startBuildMode}
                disabled={
                  !blueprintLocked || buildMode === 'build' || isStartingBuild
                }
                className={cn(
                  'px-3 py-1.5',
                  buildMode === 'build' && 'bg-primary text-primary-foreground'
                )}
                title={
                  !blueprintLocked
                    ? 'Lock blueprint first to enable Build Mode'
                    : ''
                }
              >
                <div className="flex items-center gap-1.5">
                  <Hammer className="w-3.5 h-3.5" />
                  {isStartingBuild ? 'Starting...' : 'Build'}
                </div>
              </Button>
            </div>

            {/* Blueprint Lock Status */}
            <Badge
              variant={blueprintLocked ? 'success' : 'warning'}
              className="flex items-center gap-1.5"
            >
              {blueprintLocked ? (
                <>
                  <Lock className="w-3 h-3" />
                  Blueprint Locked
                </>
              ) : (
                <>
                  <Unlock className="w-3 h-3" />
                  Blueprint Unlocked
                </>
              )}
            </Badge>

            {selectedPlanSection && (
              <Badge variant="secondary" className="flex items-center gap-2">
                <Layout className="w-4 h-4" />
                <Text size="sm" weight="medium">
                  {selectedPlanSection.sectionTitle}
                </Text>
              </Badge>
            )}
            {selectedFile && !selectedPlanSection && (
              <Badge variant="secondary" className="flex items-center gap-2">
                <File className="w-4 h-4" />
                <Text size="sm" weight="medium">
                  {selectedFile.split('/').pop()}
                </Text>
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCreatePRDialog(true)}
              title="Create Pull Request"
            >
              <GitPullRequest className="w-4 h-4 mr-2" />
              Create PR
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={openPreview}
              title="Open Preview in New Tab"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              variant={showConsole ? 'default' : 'ghost'}
              size="sm"
              onClick={toggleConsole}
              title="Toggle Console"
              className="p-2"
            >
              <Terminal className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              className="p-2"
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              title="Settings"
              aria-label="Open IDE settings"
              className="p-2"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 overflow-hidden'>
        <PanelGroup direction='horizontal' className='h-full'>
          {/* Left Sidebar - File Tree */}
          <Panel defaultSize={25} minSize={20} maxSize={40}>
            <div className="h-full border-r border-border">
              <Suspense
                fallback={
                  <LoadingState message="Loading files..." size="sm" />
                }
              >
                <FileTree
                  projectId={projectId}
                  userId={user.id}
                  onFileSelect={handleFileSelect}
                  realtimeEnabled={buildMode === 'build'}
                  buildSessionId={buildSessionId}
                />
              </Suspense>
            </div>
          </Panel>

          <PanelResizeHandle
            className="w-1 transition-colors"
            style={{
              backgroundColor: 'rgb(var(--color-border))',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(var(--color-border-hover))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(var(--color-border))';
            }}
          />

          {/* Center - Code Editor or Plan Section Editor (top) with AI Chat below */}
          <Panel defaultSize={buildMode === 'build' ? 75 : 100} minSize={30}>
            <div className='h-full flex flex-col'>
              <div className="flex-1 min-h-0 border-b border-border">
                {selectedPlanSection ? (
                  <Suspense
                    fallback={
                      <LoadingState message="Loading editor..." size="sm" />
                    }
                  >
                    {renderSectionEditor(selectedPlanSection)}
                  </Suspense>
                ) : (
                  <Suspense
                    fallback={
                      <LoadingState message="Loading code editor..." size="sm" />
                    }
                  >
                    <CodeEditor
                      projectId={projectId}
                      userId={user.id}
                      selectedFile={selectedFile}
                      onFileChange={handleFileChange}
                      onFileSave={handleFileSave}
                      content={editorContent}
                    />
                  </Suspense>
                )}
              </div>
              <div className='h-64 min-h-[12rem] max-h-[40%]'>
                {buildMode === 'build' ? (
                  <Suspense
                    fallback={
                      <div className='p-4 text-sm text-gray-500'>
                        Loading orchestrator...
                      </div>
                    }
                  >
                    <OrchestratorPanel
                      projectId={projectId}
                      sessionId={buildSessionId || undefined}
                      isActive={buildMode === 'build'}
                    />
                  </Suspense>
                ) : (
                  <Suspense
                    fallback={
                      <div className='p-4 text-sm text-gray-500'>
                        Loading chat...
                      </div>
                    }
                  >
                    <AIChatPanel
                      projectId={projectId}
                      userId={user.id}
                      selectedFile={selectedFile}
                      context={
                        chatContext ||
                        (selectedPlanSection
                          ? {
                              type: 'plan_section',
                              sectionId: selectedPlanSection.sectionId,
                              sectionTitle: selectedPlanSection.sectionTitle,
                              content: selectedPlanSection.content,
                            }
                          : undefined)
                      }
                      onContextUpdate={handleChatContextUpdate}
                    />
                  </Suspense>
                )}
              </div>
            </div>
          </Panel>

          {/* Right Sidebar - SDLC Phases (Build Mode) or Plan Tree (Plan Mode) */}
          {buildMode === 'build' ? (
            <>
              <PanelResizeHandle
            className="w-1 transition-colors"
            style={{
              backgroundColor: 'rgb(var(--color-border))',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(var(--color-border-hover))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(var(--color-border))';
            }}
          />
              <Panel defaultSize={25} minSize={20} maxSize={40}>
                <div className='h-full border-l border-gray-200 dark:border-gray-700'>
                  <Suspense
                    fallback={
                      <div className='p-4 text-sm text-gray-500'>
                        Loading SDLC phases...
                      </div>
                    }
                  >
                    <ExecutionSidebar
                      ticketId={currentTicket?.id}
                      currentTicket={currentTicket}
                      sdlcPhases={sdlcPhases}
                      phaseExecutions={phaseExecutions}
                      currentPhase={sdlcPhases.find(
                        p => p.status === 'in_progress'
                      )}
                      projectId={projectId}
                      blueprintId={blueprintId}
                      onContentUpdate={handleContentUpdate}
                      onOpenStudio={handleOpenStudio}
                    />
                  </Suspense>
                </div>
              </Panel>
            </>
          ) : null}
          {/* Plan Mode Tree removed - giving more space to editor */}
        </PanelGroup>
      </div>

      {/* Console Panel */}
      <ConsolePanel isVisible={showConsole} onToggle={toggleConsole} />

      {/* IDE Settings Modal */}
      <IDESettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSave={_settings => {
          // Settings are saved to localStorage in the modal
          // This callback can be used to trigger editor re-render if needed
          toast.success('Settings saved. Refresh editor to apply changes.');
        }}
      />

      {/* Studio UX Overlay */}
      {showStudio && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50">
          <div className="absolute inset-4 bg-white dark:bg-gray-900 rounded-lg shadow-2xl overflow-hidden">
            <StudioUX
              projectId={projectId || ''}
              blueprintId={blueprintId || undefined}
              phaseId={studioConfig.phaseId}
              stepIndex={studioConfig.stepIndex}
              initialMode={studioConfig.mode}
              initialContent={studioConfig.content}
              onClose={() => setShowStudio(false)}
              onSave={(content) => {
                setEditorContent(content);
                toast.success('Content saved');
              }}
              isFullscreen={false}
            />
          </div>
        </div>
      )}

      {/* Create PR Dialog */}
      {showCreatePRDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-default rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <Heading level={2} className="flex items-center gap-2">
                  <GitPullRequest className="w-5 h-5" />
                  Create Pull Request
                </Heading>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCreatePRDialog(false)}
                  className="p-1"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    PR Title *
                  </label>
                  <input
                    type="text"
                    value={prTitle}
                    onChange={(e) => setPRTitle(e.target.value)}
                    placeholder="feat: Add new feature"
                    className="input w-full"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary mb-2">
                    Description
                  </label>
                  <textarea
                    value={prDescription}
                    onChange={(e) => setPRDescription(e.target.value)}
                    placeholder="Describe the changes in this PR..."
                    className="input w-full h-32 resize-y"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Head Branch *
                    </label>
                    <input
                      type="text"
                      value={prBranch}
                      onChange={(e) => setPRBranch(e.target.value)}
                      placeholder="feature/my-feature"
                      className="input w-full"
                    />
                    <p className="text-xs text-secondary mt-1">
                      The branch with your changes
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Base Branch *
                    </label>
                    <input
                      type="text"
                      value={prBaseBranch}
                      onChange={(e) => setPRBaseBranch(e.target.value)}
                      placeholder="main"
                      className="input w-full"
                    />
                    <p className="text-xs text-secondary mt-1">
                      The branch to merge into
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreatePRDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleCreatePR}
                    disabled={!prTitle.trim() || !prBranch.trim() || !prBaseBranch.trim() || isCreatingPR}
                    className="flex-1"
                  >
                    {isCreatingPR ? (
                      <>Creating...</>
                    ) : (
                      <>
                        <GitPullRequest className="w-4 h-4 mr-2" />
                        Create Pull Request
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
